import { useState } from 'react';

import styled from 'styled-components';

import { Badge } from '../../../../../components/atoms/badge/badge';
import { ClienteDetail } from '../../../../../types/cliente';
import {
  formatDate,
  formatDocumento,
  formatMoneyBRL,
  formatTelefone,
} from '../../../components/format';
import { TaxasComerciaisCard } from './resumo/taxas-comerciais-card';
import { AjusteAntecipacaoCard } from './resumo/ajuste-antecipacao/ajuste-antecipacao-card';
import { D3Card } from './resumo/d3-card/d3-card';
import { ManualKycApproval } from './resumo/manual-kyc-approval';

interface Props {
  detail: ClienteDetail;
  onRefresh: () => void;
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.section`
  background: ${({ theme }) => theme.colors.bgSurface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  h3 {
    margin: 0 0 4px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;

  span:first-child {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
  span:last-child {
    color: ${({ theme }) => theme.colors.textPrimary};
    font-weight: 500;
    text-align: right;
    word-break: break-word;
  }
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const sim = (value: boolean) =>
  value ? (
    <Badge variant="success">Sim</Badge>
  ) : (
    <Badge variant="neutral">Não</Badge>
  );

const fullName = (detail: ClienteDetail): string =>
  `${detail.nome ?? ''} ${detail.sobrenome ?? ''}`.trim() || '—';

export const ResumoTab = ({ detail, onRefresh }: Props) => {
  const [isD3Enabled, setIsD3Enabled] = useState(detail.isD3Enabled);
  const isPJ = detail.tipoPessoa === 'PJ';
  const endereco = detail.endereco;
  const hasEndereco =
    endereco &&
    Boolean(
      endereco.cep || endereco.cidade || endereco.logradouro || endereco.estado
    );

  return (
    <Grid role="tabpanel" aria-labelledby="cliente-tab-resumo">
      <Card>
        <h3>Dados principais</h3>
        <Row>
          <span>Nome completo</span>
          <span>{fullName(detail)}</span>
        </Row>
        <Row>
          <span>Tipo</span>
          <span>{detail.tipoPessoa ?? '—'}</span>
        </Row>
        {isPJ && (
          <>
            <Row>
              <span>Razão social</span>
              <span>{detail.razaoSocial ?? '—'}</span>
            </Row>
            <Row>
              <span>Nome fantasia</span>
              <span>{detail.nomeFantasia ?? '—'}</span>
            </Row>
          </>
        )}
        <Row>
          <span>Documento</span>
          <span>{formatDocumento(detail.documento)}</span>
        </Row>
        {!isPJ && (
          <Row>
            <span>Data de nascimento</span>
            <span>{formatDate(detail.dataNascimento)}</span>
          </Row>
        )}
        <Row>
          <span>Tipo cliente Vendepay</span>
          <span>{detail.tipoCliente ?? '—'}</span>
        </Row>
      </Card>

      <Card>
        <h3>Contato</h3>
        <Row>
          <span>E-mail</span>
          <span>{detail.email}</span>
        </Row>
        <Row>
          <span>Telefone</span>
          <span>{formatTelefone(detail.telefone)}</span>
        </Row>
        {detail.celular2 && (
          <Row>
            <span>Telefone (2)</span>
            <span>{formatTelefone(detail.celular2)}</span>
          </Row>
        )}
        {detail.instagram && (
          <Row>
            <span>Instagram</span>
            <span>{detail.instagram}</span>
          </Row>
        )}
        {detail.facebook && (
          <Row>
            <span>Facebook</span>
            <span>{detail.facebook}</span>
          </Row>
        )}
      </Card>

      <Card>
        <h3>Endereço</h3>
        {hasEndereco ? (
          <>
            {endereco.logradouro && (
              <Row>
                <span>Logradouro</span>
                <span>
                  {endereco.logradouro}
                  {endereco.numero ? `, ${endereco.numero}` : ''}
                  {endereco.complemento ? ` — ${endereco.complemento}` : ''}
                </span>
              </Row>
            )}
            {endereco.bairro && (
              <Row>
                <span>Bairro</span>
                <span>{endereco.bairro}</span>
              </Row>
            )}
            {(endereco.cidade || endereco.estado) && (
              <Row>
                <span>Cidade/UF</span>
                <span>
                  {endereco.cidade ?? '—'}
                  {endereco.estado ? ` — ${endereco.estado}` : ''}
                </span>
              </Row>
            )}
            {endereco.cep && (
              <Row>
                <span>CEP</span>
                <span>{endereco.cep}</span>
              </Row>
            )}
          </>
        ) : (
          <Row>
            <span>—</span>
            <span>Endereço não cadastrado</span>
          </Row>
        )}
      </Card>

      <Card>
        <h3>KYC e bloqueios</h3>
        <Row>
          <span>Documentos aprovados</span>
          <span>{sim(detail.documentosAprovados)}</span>
        </Row>
        <Row>
          <span>Identidade verificada</span>
          <span>{sim(detail.identidadeVerificada)}</span>
        </Row>
        <Row>
          <span>Onboarding concluído</span>
          <span>{sim(detail.onboardingRealizado)}</span>
        </Row>
        <Row>
          <span>Bloqueios</span>
          <span>
            <BadgeRow>
              {detail.isSaleBlocked && (
                <Badge variant="danger">Vendas bloqueadas</Badge>
              )}
              {detail.isPayoutBlocked && (
                <Badge variant="danger">Saques bloqueados</Badge>
              )}
              {!detail.isSaleBlocked && !detail.isPayoutBlocked && (
                <Badge variant="success">Sem bloqueios</Badge>
              )}
            </BadgeRow>
          </span>
        </Row>
        <ManualKycApproval detail={detail} onApproved={onRefresh} />
      </Card>

      <Card>
        <h3>Comercial</h3>
        <Row>
          <span>Tempo de recebimento</span>
          <span>{detail.tempoRecebimentoDescricao ?? '—'}</span>
        </Row>
        <Row>
          <span>Como usa a plataforma</span>
          <span>{detail.comoUsarDescricao ?? '—'}</span>
        </Row>
        <Row>
          <span>Faturamento médio</span>
          <span>{formatMoneyBRL(detail.faturamentoMedio)}</span>
        </Row>
      </Card>

      <TaxasComerciaisCard
        clienteId={detail.id}
        clienteNome={fullName(detail)}
      />

      <AjusteAntecipacaoCard clienteId={detail.id} />

      <D3Card
        clienteId={detail.id}
        isD3Enabled={isD3Enabled}
        onToggle={setIsD3Enabled}
      />
    </Grid>
  );
};
